import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import * as React from "react"
import { PageHeader } from "@/components/layout"
import { DebouncedSearchInput } from "@/features/admin-grid/DebouncedSearchInput"
import { QuickFilterToggle } from "@/features/offers-admin/QuickFilterToggle"
import { ReviewAppealsAdminGrid } from "@/features/review-appeals-admin/ReviewAppealsAdminGrid"
import type { ReviewAppealsAdminInput } from "@/features/review-appeals-admin/review-appeals-admin-datasource"
import type { ListReviewAppealsInput } from "@/types/marketplace.types"

export const Route = createFileRoute("/review-appeals/")({
	component: ReviewAppealsPage,
	validateSearch: (search: Record<string, unknown>) => ({
		page: normalizePage(search.page),
		search: search.search as string | undefined,
		status: normalizeStatus(search.status),
		sort: normalizeSort(search.sort),
	}),
})

const STATUSES = ["pending", "upheld", "removed"] as const
const SORT_FIELDS = ["createdAt", "decidedAt"] as const

function normalizePage(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) return undefined
	const p = Math.floor(n)
	return p > 0 ? p : undefined
}

/** Anything not a known status is dropped rather than forwarded to the API. */
function normalizeStatus(raw: unknown): (typeof STATUSES)[number] | undefined {
	return STATUSES.find((s) => s === raw)
}

/** `field:direction`, both whitelisted; anything else falls back to the default. */
function normalizeSort(raw: unknown): string | undefined {
	if (typeof raw !== "string") return undefined
	const [field, direction] = raw.split(":")
	const validField = SORT_FIELDS.find((f) => f === field)
	if (!validField) return undefined
	if (direction !== "asc" && direction !== "desc") return undefined
	return `${validField}:${direction}`
}

function ReviewAppealsPage() {
	const searchParams = useSearch({ from: "/review-appeals/" })
	const navigate = useNavigate()

	const pageIndex = (searchParams.page ?? 1) - 1

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/review-appeals",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) =>
			updateFilters({ search: value || undefined, page: undefined }),
		[updateFilters],
	)

	const toggleStatus = React.useCallback(
		(status: string) =>
			updateFilters({
				status: searchParams.status === status ? undefined : status,
				page: undefined,
			}),
		[updateFilters, searchParams.status],
	)

	const [sortBy, sortOrder] = React.useMemo(() => {
		const [by, order] = (searchParams.sort ?? "createdAt:desc").split(":")
		return [
			(by || "createdAt") as ListReviewAppealsInput["sortBy"],
			(order || "desc") as ListReviewAppealsInput["sortOrder"],
		]
	}, [searchParams.sort])

	const input = React.useMemo<ReviewAppealsAdminInput>(
		() => ({
			search: searchParams.search,
			status: searchParams.status as ReviewAppealsAdminInput["status"],
			sortBy,
			sortOrder,
		}),
		[searchParams.search, searchParams.status, sortBy, sortOrder],
	)

	const handlePageChange = React.useCallback(
		(nextIndex: number) =>
			updateFilters({ page: nextIndex === 0 ? undefined : nextIndex + 1 }),
		[updateFilters],
	)

	const handleSortChange = React.useCallback(
		(
			by: ListReviewAppealsInput["sortBy"],
			order: ListReviewAppealsInput["sortOrder"],
		) =>
			updateFilters({
				sort:
					by === "createdAt" && order === "desc" ? undefined : `${by}:${order}`,
			}),
		[updateFilters],
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader
				title="Review appeals"
				description="Decided from the order page: keep the review, or remove it from the appellant's average"
			/>

			<ReviewAppealsAdminGrid
				input={input}
				pageIndex={pageIndex}
				onPageChange={handlePageChange}
				onSortChange={handleSortChange}
				toolbar={
					<>
						<DebouncedSearchInput
							defaultValue={searchParams.search ?? ""}
							onDebouncedChange={handleDebouncedSearch}
							placeholder="Order number or username"
						/>
						<QuickFilterToggle
							active={searchParams.status === "pending"}
							onToggle={() => toggleStatus("pending")}
						>
							Pending
						</QuickFilterToggle>
						<QuickFilterToggle
							active={searchParams.status === "upheld"}
							onToggle={() => toggleStatus("upheld")}
						>
							Review kept
						</QuickFilterToggle>
						<QuickFilterToggle
							active={searchParams.status === "removed"}
							onToggle={() => toggleStatus("removed")}
						>
							Review removed
						</QuickFilterToggle>
					</>
				}
			/>
		</div>
	)
}
